<template>
    <AppLayout>
        <!-- Featured image  -->
        <div
            v-if="featuredImages.length"
            class="relative"
        >
            <HomeSlider :slider-images="featuredImages" />
            <!-- Trendy Hero Section -->
            <section class="relative bg-gradient-to-br from-green-50 to-green-100 py-20 px-6 text-center">
                <div class="max-w-4xl mx-auto">
                    <h1 class="text-3xl md:text-6xl font-extrabold text-green-800 leading-tight mb-6">
                        Unlock Wholesale Power.<br />
                        One Platform. Thousands of Shops.
                    </h1>
                    <p class="text-lg md:text-2xl text-gray-700 mb-8">
                        Join the largest B2B platform connecting wholesalers & shop owners in T&T.
                    </p>
                    <inertia-link
                        :href="route('register')"
                        as="button"
                        class="inline-flex items-center px-6 py-3 bg-green-600 text-white text-lg font-semibold rounded-full shadow-lg hover:bg-green-500 transition"
                    >
                        Register for Free
                        <RightArrowIcon class="w-5 ml-2" />
                    </inertia-link>
                </div>
            </section>
        </div>

        <!-- Animated Stats Section -->
        <section class="bg-gray-50 py-12 shadow-inner">
            <div class="container mx-auto px-4 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
                <div>
                    <img
                        src="/storage/icons/icon-wholesalers-64.png"
                        class="h-12 mx-auto mb-2"
                    />
                    <h2 class="text-2xl font-bold text-green-600">30K+</h2>
                    <p class="text-gray-600">Retailers</p>
                </div>
                <div>
                    <img
                        src="/storage/icons/icon-credit-card-64.png"
                        class="h-12 mx-auto mb-2"
                    />
                    <h2 class="text-2xl font-bold text-green-600">15K</h2>
                    <p class="text-gray-600">Credit Support</p>
                </div>
                <div>
                    <img
                        src="/storage/icons/icon-products-64.png"
                        class="h-12 mx-auto mb-2"
                    />
                    <h2 class="text-2xl font-bold text-green-600">25K+</h2>
                    <p class="text-gray-600">Products Listed</p>
                </div>
            </div>
        </section>

        <!-- Promo Strip -->
        <section class="bg-green-700 text-white py-4 text-center">
            <p class="text-lg font-medium">
                🚀 Boost your store's profit – Upload KYC & unlock exclusive wholesale pricing
            </p>
        </section>

        <section class="py-16 bg-gray-50">
            <div class="container mx-auto px-6">
                <h2 class="text-3xl font-bold text-center text-green-700 mb-10">Shop by Category</h2>
                <swiper
                    ref="swiperCategoryRef"
                    :options="categorySwiperOptions"
                    class="pb-6"
                    @ready="onSwiperReady('swiperCategoryRef', $event)"
                    @mouseenter.native="pauseAutoplay('swiperCategoryRef')"
                    @mouseleave.native="resumeAutoplay('swiperCategoryRef')"
                >
                    <swiper-slide
                        v-for="category in categories"
                        :key="category.id"
                    >
                        <div class="flex flex-col items-center justify-center p-2">
                            <div class="mb-4">
                                <img
                                    :src="
                                        category.images.length
                                            ? category.images[0].image_path
                                            : `https://ui-avatars.com/api/?name=${category.name}&color=7F9CF5&background=EBF4FF`
                                    "
                                    class="p-1 rounded-full cursor-pointer h-14 w-14 md:w-28 md:h-28 ring"
                                    @click="goto('products.index', { categoryIds: [category.id] })"
                                />
                            </div>
                            <div class="text-center">
                                {{ category.name }}
                            </div>
                        </div>
                    </swiper-slide>
                </swiper>
            </div>
        </section>

        <!-- KYC Section -->
        <section
            id="kyc"
            class="py-16 px-4 md:px-20 text-center bg-green-50"
        >
            <div class="container mx-auto px-6">
                <h2 class="text-3xl font-bold text-green-700 mb-4">Complete KYC to Unlock Pricing</h2>
                <p class="text-gray-700 text-lg mb-6">
                    Submit your shop details to get access to bulk pricing. Only visible to verified business users.
                </p>
                <p class="text-sm text-red-500 mb-4">Docs needed: Shop Reg. Certificate, ID, VAT*</p>
                <inertia-link
                    :href="route('register')"
                    as="button"
                    class="px-6 py-2 text-white bg-green-600 hover:bg-green-500 rounded-full text-lg"
                >
                    Upload KYC
                </inertia-link>
            </div>
        </section>

        <!-- Trending Products -->
        <section class="py-16 bg-gray-50">
            <div class="container mx-auto px-6">
                <h2 class="text-3xl font-bold text-center text-green-700 mb-10">Trending Products</h2>
                <swiper
                    ref="swiperProductRef"
                    :options="productsSwiperOptions"
                    class="pb-6"
                    @ready="onSwiperReady('swiperProductRef', $event)"
                    @mouseenter.native="pauseAutoplay('swiperProductRef')"
                    @mouseleave.native="resumeAutoplay('swiperProductRef')"
                >
                    <swiper-slide
                        v-for="(product, index) in firstSix(products)"
                        :key="'new-products-' + index"
                    >
                        <ProductCard
                            :product="product"
                            :show-price="false"
                            :show-counter="false"
                        />
                    </swiper-slide>
                </swiper>
            </div>
        </section>

        <!-- Trust Badges / Testimonials -->
        <section class="bg-green-100 py-16 px-4 px-6">
            <div class="container mx-auto px-6">
                <h2 class="text-center text-3xl font-bold text-green-800 mb-10">What Our Users Say</h2>
                <div class="grid gap-6 md:grid-cols-3">
                    <div class="bg-white p-6 rounded-lg shadow">
                        <p class="text-gray-600 italic">
                            “This platform helped me grow my shop 3x faster. Easy to use and reliable!”
                        </p>
                        <p class="mt-4 font-semibold text-green-700">— Ayesha, Retailer</p>
                    </div>
                    <div class="bg-white p-6 rounded-lg shadow">
                        <p class="text-gray-600 italic">
                            “We’ve found top suppliers with great deals. Everything is smooth and transparent.”
                        </p>
                        <p class="mt-4 font-semibold text-green-700">— Kamal, Wholesaler</p>
                    </div>
                    <div class="bg-white p-6 rounded-lg shadow">
                        <p class="text-gray-600 italic">
                            “Highly recommended for new shop owners. The KYC makes the pricing exclusive.”
                        </p>
                        <p class="mt-4 font-semibold text-green-700">— Reena, New Store Owner</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Abstract Decorative Section for Visual Interest -->
        <section class="relative bg-gradient-to-tr from-green-50 via-white to-green-100 py-16 px-6">
            <div class="container mx-auto px-6">
                <div
                    class="absolute inset-0 bg-[url('/images/abstract-wave-bg.svg')] bg-no-repeat bg-contain bg-right-top opacity-10 pointer-events-none"
                ></div>
                <div class="relative max-w-4xl mx-auto text-center">
                    <h2 class="text-3xl md:text-5xl font-extrabold text-green-800 font-display mb-4">
                        Discover Smarter Shopping
                    </h2>
                    <p class="text-lg md:text-xl text-green-900 mb-6">
                        Explore categories and top brands tailored to grow your business faster.
                    </p>
                </div>
            </div>
        </section>

        <HowTo class="container mx-auto px-6" />
        <!-- Call to Action Above Footer -->
        <section class="relative bg-gradient-to-br from-green-100 via-green-200 to-green-300 py-12 px-6 text-center">
            <div class="container-footer mx-auto px-6">
                <div
                    class="absolute inset-0 bg-[url('/images/footer-abstract-bg.svg')] bg-no-repeat bg-cover opacity-10"
                ></div>
                <div class="relative max-w-4xl mx-auto">
                    <h2 class="text-3xl md:text-5xl font-extrabold text-green-800 font-display mb-4">
                        Join 30,000+ Smart Retailers
                    </h2>
                    <p class="text-lg md:text-xl text-green-900 mb-6">
                        Register now to unlock exclusive wholesale prices, fast delivery, and trusted brands at your
                        fingertips.
                    </p>
                    <inertia-link
                        :href="route('register')"
                        as="button"
                        class="inline-block px-8 py-3 bg-green-700 text-white rounded-full text-lg font-semibold shadow-md hover:bg-green-600 transition"
                    >
                        Get Started Now
                    </inertia-link>
                </div>
            </div>
        </section>
    </AppLayout>
</template>

<script>
    import AppLayout from '@/Layouts/AppLayout.vue';
    import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
    import 'swiper/css/swiper.css';
    import RightArrowIcon from '@/Components/Icons/RightArrowIcon.vue';
    import HowTo from '@/Components/HowTo.vue';
    import HomeSlider from '@/Components/HomeSlider.vue';
    import ProductCard from '@/Components/ProductCard.vue';
    import common from '../common';

    export default {
        name: 'Dashboard',
        components: {
            AppLayout,
            Swiper,
            SwiperSlide,
            HomeSlider,
            ProductCard,
            RightArrowIcon,
            HowTo,
        },

        metaInfo: {
            title: 'Gumtt B2B Ecommerce',
            meta: [
                {
                    name: 'description',
                    content: 'Browse our top trending products.',
                },
                {
                    property: 'og:title',
                    content: 'Product Page - EcomTT',
                },
            ],
        },

        // metaInfo() {
        //     return {
        //         title: 'B2B Shopping',
        //         titleTemplate: 'Ecom - %s',
        //     };
        // },

        props: {
            products: {
                type: [Object, Array],
                required: true,
            },
            categories: {
                type: [Object, Array],
                required: true,
            },

            featuredImages: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                common,
            };
        },

        computed: {
            categorySwiperOptions() {
                return this.defaultSwiperOptions;
            },

            productsSwiperOptions() {
                return {
                    ...this.defaultSwiperOptions,
                    spaceBetween: 20,
                    autoplay: {
                        ...this.defaultSwiperOptions.autoplay,
                        delay: 2000,
                    },
                };
            },
        },

        methods: {
            firstSix(array) {
                if (array.length > 6) return array.slice(0, 6);
                return array;
            },

            scrollBelow() {
                const element = document.getElementById('kyc');
                const headerOffset = 100;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            },
        },
    };
</script>

<style>
    .banner-height {
        height: calc(100vh - 65px);
    }
    .container-footer {
        max-width: 1600px;
    }

    .container {
        max-width: 1200px;
    }

    @media only screen and (max-width: 600px) {
        .banner-height {
            height: 30vh;
        }
    }
</style>
